import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  serverTimestamp,
  type QueryConstraint,
  type DocumentData,
  type QueryDocumentSnapshot,
  Timestamp,
} from 'firebase/firestore'
import { db } from './config'
import type { AuditLog, UserRole } from '@/types'

// ============================================
// GENERIC FIRESTORE HELPERS
// ============================================

export interface QueryOptions {
  orderByField?: string
  orderDirection?: 'asc' | 'desc'
  limitCount?: number
  startAfterDoc?: QueryDocumentSnapshot<DocumentData>
}

export interface PaginatedResult<T> {
  data: T[]
  lastDoc: QueryDocumentSnapshot<DocumentData> | null
  hasMore: boolean
}

// Convert Firestore timestamp to Date
export function toDate(timestamp: unknown): Date | null {
  if (!timestamp) return null
  if (timestamp instanceof Timestamp) return timestamp.toDate()
  if (timestamp instanceof Date) return timestamp
  if (typeof timestamp === 'string') return new Date(timestamp)
  return null
}

export type DateFormat = 'short' | 'long' | 'time' | 'datetime' | 'date' | 'monthShort' | 'relative'

// Format date for display
export function formatDate(date: Date | null | undefined, format: DateFormat = 'short', timeZone?: string): string {
  if (!date) return ''

  if (format === 'relative') return formatRelativeTime(date)

  const optionsMap: Record<Exclude<DateFormat, 'relative'>, Intl.DateTimeFormatOptions> = {
    short: { month: 'short', day: 'numeric', year: 'numeric' },
    long: { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' },
    time: { hour: 'numeric', minute: '2-digit' },
    datetime: { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' },
    date: { month: 'numeric', day: 'numeric', year: 'numeric' },
    monthShort: { month: 'short', day: 'numeric' },
  }

  return new Intl.DateTimeFormat('en-US', {
    ...optionsMap[format],
    ...(timeZone ? { timeZone } : {}),
  }).format(date)
}

export function getCalendarDateKey(date: Date | null | undefined, timeZone?: string): string {
  if (!date) return ''

  const formatter = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  })

  const parts = formatter.formatToParts(date)
  const year = parts.find(part => part.type === 'year')?.value
  const month = parts.find(part => part.type === 'month')?.value
  const day = parts.find(part => part.type === 'day')?.value

  return year && month && day ? `${year}-${month}-${day}` : ''
}

function parseCalendarDateKey(dateKey: string): { year: number; month: number; day: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey)
  if (!match) return null
  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
  }
}

export function addDaysToCalendarDateKey(dateKey: string, days: number): string {
  const parts = parseCalendarDateKey(dateKey)
  if (!parts) return ''
  const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + days, 12))
  return date.toISOString().slice(0, 10)
}

export function getCalendarDateKeyDayOfWeek(dateKey: string): number {
  const parts = parseCalendarDateKey(dateKey)
  if (!parts) return 0
  return new Date(Date.UTC(parts.year, parts.month - 1, parts.day, 12)).getUTCDay()
}

export type CalendarDateKeyFormat = 'day' | 'weekdayShort' | 'month' | 'monthYear' | 'monthShort' | 'long'

export function formatCalendarDateKey(dateKey: string, format: CalendarDateKeyFormat = 'long'): string {
  const parts = parseCalendarDateKey(dateKey)
  if (!parts) return ''

  const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day, 12))
  const optionsMap: Record<CalendarDateKeyFormat, Intl.DateTimeFormatOptions> = {
    day: { day: 'numeric' },
    weekdayShort: { weekday: 'short' },
    month: { month: 'short' },
    monthYear: { month: 'long', year: 'numeric' },
    monthShort: { month: 'short', day: 'numeric' },
    long: { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' },
  }

  return new Intl.DateTimeFormat('en-US', {
    ...optionsMap[format],
    timeZone: 'UTC',
  }).format(date)
}

function getTimeZoneOffsetMs(date: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date)

  const value = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find(part => part.type === type)?.value || 0)
  const asUtc = Date.UTC(
    value('year'),
    value('month') - 1,
    value('day'),
    value('hour'),
    value('minute'),
    value('second')
  )

  return asUtc - date.getTime()
}

export function getStartOfCalendarDateKey(dateKey: string, timeZone?: string): Date | null {
  const parts = parseCalendarDateKey(dateKey)
  if (!parts) return null

  if (!timeZone) {
    return new Date(parts.year, parts.month - 1, parts.day, 0, 0, 0, 0)
  }

  const targetUtc = Date.UTC(parts.year, parts.month - 1, parts.day, 0, 0, 0, 0)
  const firstPass = new Date(targetUtc - getTimeZoneOffsetMs(new Date(targetUtc), timeZone))
  return new Date(targetUtc - getTimeZoneOffsetMs(firstPass, timeZone))
}

export function isSameCalendarDay(dateA: Date | null | undefined, dateB: Date | null | undefined, timeZone?: string): boolean {
  if (!dateA || !dateB) return false
  return getCalendarDateKey(dateA, timeZone) === getCalendarDateKey(dateB, timeZone)
}

// Format relative time (e.g., "5 minutes ago", "2 hours ago")
export function formatRelativeTime(date: Date | null | undefined): string {
  if (!date) return ''

  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (diffInSeconds < 60) return 'Just now'
  if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60)
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`
  }
  if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600)
    return `${hours} hour${hours > 1 ? 's' : ''} ago`
  }
  if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400)
    return `${days} day${days > 1 ? 's' : ''} ago`
  }

  return formatDate(date, 'short')
}

// ============================================
// AUDIT LOGGING
// ============================================

interface AuditLogInput {
  agencyId: string
  userId: string
  userEmail: string
  userRole?: UserRole
  action: AuditLog['action']
  resource: AuditLog['resource']
  resourceId: string
  description?: string
  details?: Record<string, unknown>
  previousValue?: Record<string, unknown>
  previousValues?: Record<string, unknown>
  newValue?: Record<string, unknown>
  newValues?: Record<string, unknown>
  changedFields?: string[]
}

export async function createAuditLog(input: AuditLogInput): Promise<void> {
  try {
    // Strip undefined values to prevent Firestore rejection
    const clean: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(input)) {
      if (v !== undefined) clean[k] = v
    }
    clean.timestamp = serverTimestamp()

    await addDoc(collection(db, 'auditLogs'), clean)
  } catch (error) {
    // Log error but don't throw - audit logging shouldn't break the main operation
    console.error('Failed to create audit log:', error)
  }
}

// Helper to detect changed fields between two objects
export function getChangedFields(
  previous: Record<string, unknown>,
  next: Record<string, unknown>
): string[] {
  const changedFields: string[] = []
  const allKeys = new Set([...Object.keys(previous), ...Object.keys(next)])

  for (const key of allKeys) {
    if (JSON.stringify(previous[key]) !== JSON.stringify(next[key])) {
      changedFields.push(key)
    }
  }

  return changedFields
}

// ============================================
// COLLECTION REFERENCES
// ============================================

export const collections = {
  agencies: 'agencies',
  users: 'users',
  clients: 'clients',
  authorizations: 'authorizations',
  plansOfCare: 'plansOfCare',
  visits: 'visits',
  visitNotes: 'visitNotes',
  incidents: 'incidents',
  clientNotes: 'clientNotes',
  clientDocuments: 'clientDocuments',
  messageThreads: 'messageThreads',
  messages: 'messages',
  notifications: 'notifications',
  auditLogs: 'auditLogs',
  systemAlerts: 'systemAlerts',
  waitlist: 'waitlist',
} as const

// ============================================
// GENERIC CRUD OPERATIONS
// ============================================

// Get single document by ID
export async function getDocument<T>(
  collectionName: string,
  documentId: string
): Promise<T | null> {
  const docRef = doc(db, collectionName, documentId)
  const docSnap = await getDoc(docRef)

  if (!docSnap.exists()) return null

  return { id: docSnap.id, ...docSnap.data() } as T
}

// Query documents with agency isolation
export async function queryDocuments<T>(
  collectionName: string,
  agencyId: string,
  additionalConstraints: QueryConstraint[] = [],
  options: QueryOptions = {}
): Promise<PaginatedResult<T>> {
  const constraints: QueryConstraint[] = [
    where('agencyId', '==', agencyId),
    ...additionalConstraints,
  ]

  if (options.orderByField) {
    constraints.push(orderBy(options.orderByField, options.orderDirection || 'desc'))
  }

  const pageLimit = options.limitCount || 25
  constraints.push(limit(pageLimit + 1)) // Get one extra to check if there's more

  if (options.startAfterDoc) {
    constraints.push(startAfter(options.startAfterDoc))
  }

  const q = query(collection(db, collectionName), ...constraints)
  const snapshot = await getDocs(q)

  const docs = snapshot.docs.slice(0, pageLimit)
  const hasMore = snapshot.docs.length > pageLimit

  return {
    data: docs.map(doc => ({ id: doc.id, ...doc.data() } as T)),
    lastDoc: docs[docs.length - 1] || null,
    hasMore,
  }
}

// Create document with metadata
export async function createDocument<T extends DocumentData>(
  collectionName: string,
  data: T,
  userId: string
): Promise<string> {
  const docRef = await addDoc(collection(db, collectionName), {
    ...data,
    createdAt: serverTimestamp(),
    createdBy: userId,
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  })

  return docRef.id
}

// Update document with metadata
export async function updateDocument(
  collectionName: string,
  documentId: string,
  data: Partial<DocumentData>,
  userId: string
): Promise<void> {
  const docRef = doc(db, collectionName, documentId)

  await updateDoc(docRef, {
    ...data,
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  })
}

// Soft delete document
export async function softDeleteDocument(
  collectionName: string,
  documentId: string,
  userId: string
): Promise<void> {
  const docRef = doc(db, collectionName, documentId)

  await updateDoc(docRef, {
    isDeleted: true,
    deletedAt: serverTimestamp(),
    deletedBy: userId,
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  })
}

// ============================================
// AGENCY SETTINGS
// ============================================

export async function getAgencySettings(agencyId: string) {
  const docRef = doc(db, 'agencies', agencyId, 'settings', 'config')
  const docSnap = await getDoc(docRef)

  if (!docSnap.exists()) return null

  return docSnap.data()
}
